import { ChevronRightIcon } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { GauInText, GauTerm } from "core/billing";
import {
  type AutoTopupDisableReason,
  useCreatePortalSessionMutation,
  useGetAutoTopupConfigQuery,
  useManualTopupChargeMutation,
  useUpdateAutoTopupConfigMutation,
} from "store/slices/reduxApi/billing";
import {
  sendErrorUINotification,
  sendSuccessUINotification,
} from "utils/notification";

import {
  type AutoErrors,
  type AutoFormState,
  PACK_MAX,
  PACK_MIN,
  autoFormEqual,
  autoFormFromServer,
  formatUsd,
  isWholeNumber,
  validateAutoForm,
} from "./AutoTopUpForm";
import { formatGaus } from "./gauFormat";

// Dev tools live on /usage and /spending behind ?devTools=1, not this panel.

/**
 * Shared RTK Query fixedCacheKey for the manualTopupCharge mutation. Exported
 * so other components (e.g. the Checkout button in ManagePlanModal) can read
 * the same isLoading state without owning the mutation trigger:
 *   const [, { isLoading }] = useManualTopupChargeMutation({
 *     fixedCacheKey: MANUAL_TOPUP_CACHE_KEY,
 *   });
 */
export const MANUAL_TOPUP_CACHE_KEY = "manual-topup-charge";

function formatDate(iso: string | null) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function disabledReasonMessage(reason: AutoTopupDisableReason | null): string {
  switch (reason) {
    case "card_declined":
      return "Your card was declined. Update your payment method to re-enable top up.";
    case "no_payment_method":
      return "Top up was disabled because we no longer have a valid payment method on file.";
    case "plan_change":
      return "Top up was disabled because your plan changed.";
    default:
      return "Top up was disabled.";
  }
}

function Banner({
  tone,
  children,
}: {
  tone: "info" | "warning" | "error";
  children: React.ReactNode;
}) {
  const bg =
    tone === "error" ? "#fde8e8" : tone === "warning" ? "#fef3c7" : "#dbeafe";
  const fg =
    tone === "error" ? "#991b1b" : tone === "warning" ? "#854d0e" : "#1e3a8a";
  return (
    <div
      data-test={`auto-topup-banner-${tone}`}
      style={{
        background: bg,
        color: fg,
        borderRadius: 6,
        padding: "8px 12px",
        fontSize: 12,
        lineHeight: "16px",
      }}
    >
      {children}
    </div>
  );
}

export function AutoTopUpPanel() {
  const { data, isLoading } = useGetAutoTopupConfigQuery();
  const [updateConfig, { isLoading: isSaving }] =
    useUpdateAutoTopupConfigMutation();
  const [createPortalSession] = useCreatePortalSessionMutation();
  // fixedCacheKey lets sibling components (notably the Checkout button in
  // ManagePlanModal) observe the same isLoading state via the same hook —
  // they call useManualTopupChargeMutation({ fixedCacheKey: MANUAL_TOPUP_CACHE_KEY })
  // to read the in-flight flag without owning the trigger.
  const [manualCharge, { isLoading: isCharging }] =
    useManualTopupChargeMutation({ fixedCacheKey: MANUAL_TOPUP_CACHE_KEY });

  // Manual top up subsection state — chips of standard pack sizes plus a
  // "Custom amount" chip that opens a numeric input for any value in the
  // 20..9999 range. Three input modes:
  //   - 'none'    — nothing picked yet (default & post-charge resting state).
  //                 No price preview, no Add button. Admin has to commit to
  //                 a value before the action surface appears.
  //   - 'preset'  — a chip is active; manualPack holds the value.
  //   - 'custom'  — Custom amount input is active; customValue holds it.
  // customValue is retained when switching back to a preset chip so the next
  // click on Custom amount restores the typed value.
  const MANUAL_PACKS = [50, 100, 250, 500, 1000, 2500] as const;
  const MANUAL_CUSTOM_MIN = 20;
  const MANUAL_CUSTOM_MAX = 9999;
  const [manualKind, setManualKind] = useState<"none" | "preset" | "custom">(
    "none",
  );
  const [manualPack, setManualPack] = useState<number | null>(null);
  const [customValue, setCustomValue] = useState<string>("");
  const [manualError, setManualError] = useState<string | null>(null);
  const [manualSuccess, setManualSuccess] = useState<string | null>(null);

  const customParsed = Number(customValue);
  const customIsWhole =
    customValue.trim() !== "" &&
    Number.isFinite(customParsed) &&
    Number.isInteger(customParsed);
  const customError: string | null = (() => {
    if (manualKind !== "custom") return null;
    if (customValue.trim() === "") return "Enter an amount";
    if (!customIsWhole) return "Whole number required";
    if (customParsed < MANUAL_CUSTOM_MIN)
      return `Minimum ${MANUAL_CUSTOM_MIN.toLocaleString()} GAUs`;
    if (customParsed > MANUAL_CUSTOM_MAX)
      return `Maximum ${MANUAL_CUSTOM_MAX.toLocaleString()} GAUs`;
    return null;
  })();

  const effectiveCredits: number | null =
    manualKind === "preset"
      ? manualPack
      : manualKind === "custom" && customError === null
        ? customParsed
        : null;

  // Two-click commit for the charge button — first click "arms" the action and
  // re-renders the bar as Cancel + Confirm; the actual charge fires on the
  // second click. Prevents accidental real-money clicks.
  const [confirmingCharge, setConfirmingCharge] = useState(false);
  // Reset confirmation whenever the input mode, selected amount, or
  // success/error state changes — admin must re-aim before each commit.
  useEffect(() => {
    setConfirmingCharge(false);
  }, [manualKind, manualPack, customValue, manualError, manualSuccess]);

  const handleSelectPreset = (pack: number) => {
    setManualKind("preset");
    setManualPack(pack);
  };
  const handleSelectCustom = () => {
    setManualKind("custom");
    // First-time entry into custom mode: seed with the current preset value
    // (if a preset is selected) or the minimum so the input always opens
    // with a sensible starting number. Subsequent entries restore
    // whatever the admin previously typed.
    if (customValue.trim() === "") {
      setCustomValue(String(manualPack ?? MANUAL_PACKS[1]));
    }
  };

  const [form, setForm] = useState<AutoFormState>({
    enabled: false,
    packCredits: "",
    monthlyCreditCap: "",
    triggerBufferCredits: "",
  });

  const [expanded, setExpanded] = useState(false);

  // Sync form from server when data loads/changes. `autoFormFromServer` bakes
  // in the shared first-run defaults so a fresh org sees the same starter
  // values here as in the Manage Plan modal and /spending — single source of
  // truth, no surface-by-surface drift.
  useEffect(() => {
    if (!data) return;
    setForm(autoFormFromServer(data));
  }, [data]);

  const errors: AutoErrors = useMemo(() => validateAutoForm(form), [form]);
  const hasErrors = Object.keys(errors).length > 0;

  if (isLoading) {
    return (
      <div data-test="auto-topup-panel-loading" style={{ padding: 16 }}>
        Loading top up…
      </div>
    );
  }

  if (!data) {
    return null;
  }

  // Canceling (cancel_at in future): render a read-only banner. Auto-topup
  // charges are suppressed server-side; we let the admin see the state but
  // not act on it. Re-enabling requires removing the scheduled cancellation.
  if (data.available === false && data.unavailable_reason === "canceling") {
    return (
      <div
        data-test="auto-topup-canceling-banner"
        style={{
          padding: 12,
          background: "#fef3c7",
          color: "#854d0e",
          border: "1px solid #fde68a",
          borderRadius: 6,
          fontSize: 13,
          lineHeight: "18px",
        }}
      >
        <strong>Top up is paused.</strong> Your subscription is scheduled to
        cancel. Existing top up <GauTerm plural /> remain usable until the
        subscription ends. To re-enable top up, remove the scheduled
        cancellation in the billing portal first.
      </div>
    );
  }

  // Canceled / plan_ineligible / trialing / missing billing → hide the
  // panel entirely (no meaningful action the admin can take here).
  if (data.available === false) {
    return null;
  }

  const pack = Number(form.packCredits);
  const cap = Number(form.monthlyCreditCap);
  const unitAmountCents = data.unit_amount_cents;

  const packPreview =
    isWholeNumber(form.packCredits) && pack > 0
      ? formatUsd(pack * unitAmountCents)
      : null;
  const capPreview =
    isWholeNumber(form.monthlyCreditCap) && cap > 0
      ? formatUsd(cap * unitAmountCents)
      : null;

  const showNoPaymentBanner = data.has_default_payment_method === false;
  const showAutoDisabledBanner =
    data.enabled === false &&
    data.last_disabled_reason !== null &&
    data.last_disabled_reason !== "admin";
  // cap_skip_pending fires when cap-room is below the partial-topup floor — auto top-up
  // won't fire any more charges this cycle, even if topup_remaining drops to zero. The
  // server computes this; we surface it as an amber pill (collapsed) and a banner with
  // a Raise-cap action (expanded). Falls back to the older capReached heuristic when the
  // server hasn't shipped the flag yet.
  const showCapSkipBanner =
    data.enabled === true && data.cap_skip_pending === true;
  const capRoomRemaining =
    data.enabled === true &&
    data.stats !== null &&
    data.monthly_credit_cap !== null
      ? Math.max(
          0,
          data.monthly_credit_cap - data.stats.credits_purchased_this_cycle,
        )
      : null;

  const handleOpenPortal = async () => {
    try {
      const result = await createPortalSession({
        flow: "payment_method_update",
        returnTo: "/usage",
      }).unwrap();
      window.location.assign(result.portalUrl);
    } catch {
      // noop - parent surfaces errors elsewhere
    }
  };

  const handleSave = async () => {
    if (hasErrors || isSaving) return;
    // Persist field values regardless of `enabled` so an admin can toggle
    // auto-topup off without losing their previously chosen pack/cap/trigger.
    // When the user re-enables, the same values are restored.
    try {
      await updateConfig({
        enabled: form.enabled,
        packCredits: isWholeNumber(form.packCredits)
          ? Number(form.packCredits)
          : null,
        monthlyCreditCap: isWholeNumber(form.monthlyCreditCap)
          ? Number(form.monthlyCreditCap)
          : null,
        triggerBufferCredits: isWholeNumber(form.triggerBufferCredits)
          ? Number(form.triggerBufferCredits)
          : null,
      }).unwrap();
      sendSuccessUINotification({
        message: form.enabled
          ? "Auto top-up settings saved"
          : "Auto top-up turned off",
        duration: 5,
      });
    } catch {
      sendErrorUINotification({
        message: "Failed to save auto top-up settings",
        duration: 5,
      });
    }
  };

  const handleManualCharge = async () => {
    if (isCharging) return;
    if (effectiveCredits == null) return;
    const credits = effectiveCredits;
    setManualError(null);
    setManualSuccess(null);
    try {
      const result = await manualCharge({
        credits,
        // crypto.randomUUID is widely supported; fall back to a timestamp-based
        // key for (very old) browsers that don't have it. Either way, the same
        // click can't double-bill — the server collapses duplicate keys.
        idempotencyKey:
          typeof crypto !== "undefined" && "randomUUID" in crypto
            ? crypto.randomUUID()
            : `${Date.now()}-${Math.random()}`,
      }).unwrap();
      if (result.status === "succeeded") {
        setManualSuccess(
          `Added ${credits.toLocaleString()} GAUs to your shared pool.`,
        );
        // Collapse the panel back to the resting state (no chip selected,
        // no input visible, no Add button) so the next interaction starts
        // fresh and the typed custom value isn't implicitly carried into
        // a new charge.
        setManualKind("none");
        setManualPack(null);
        setCustomValue("");
      } else if (result.status === "pending_authentication") {
        setManualError(
          "Your card needs additional verification (3D Secure). Open the billing portal to complete the charge.",
        );
      } else {
        setManualError(
          result.failureMessage ?? "The charge could not be completed.",
        );
      }
    } catch (err: unknown) {
      const data = (err as { data?: { error?: { message?: string } } }).data;
      setManualError(
        data?.error?.message ?? "The charge could not be completed.",
      );
    } finally {
      // Always tear down the confirmation state after a charge attempt — the
      // success/error banners now carry the outcome, and we want subsequent
      // clicks to require re-arming.
      setConfirmingCharge(false);
    }
  };

  // Wraps handleManualCharge with the two-click confirm pattern. First click
  // arms; second click fires.
  const handleChargeButtonClick = () => {
    if (isCharging || !data?.has_default_payment_method) return;
    if (!confirmingCharge) {
      setConfirmingCharge(true);
      return;
    }
    void handleManualCharge();
  };

  // Source of truth for "what should the form look like for this server data?"
  // — shared with the modal/spending surfaces via `autoFormFromServer`, so
  // Discard restores the exact same baseline and isDirty doesn't lie.
  const initialForm = autoFormFromServer(data);

  const handleDiscard = () => {
    setForm(initialForm);
  };

  const isDirty: boolean = !autoFormEqual(initialForm, form);

  const summaryText: string = (() => {
    if (showNoPaymentBanner) return "Add a payment method to enable";
    if (
      data.enabled === false &&
      data.last_disabled_reason !== null &&
      data.last_disabled_reason !== "admin"
    ) {
      const reasonShort =
        data.last_disabled_reason === "card_declined"
          ? "card declined"
          : data.last_disabled_reason === "no_payment_method"
            ? "no payment method"
            : data.last_disabled_reason === "plan_change"
              ? "plan changed"
              : "disabled";
      return `Auto off — ${reasonShort}`;
    }
    if (!data.enabled) return "Auto off";
    const pack = data.pack_credits;
    const cap = data.monthly_credit_cap;
    if (pack && cap) return `Auto on · ${pack} GAUs per top up · cap ${cap}/mo`;
    if (pack) return `Auto on · ${pack} GAUs per top up`;
    return "Auto on";
  })();

  return (
    <div
      data-test="auto-topup-panel"
      style={{
        border: "1px solid #e8eaed",
        borderRadius: 8,
        background: "#fff",
        overflow: "hidden",
      }}
    >
      <button
        type="button"
        data-test="auto-topup-toggle"
        aria-expanded={expanded}
        onClick={() => setExpanded((v) => !v)}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%",
          padding: "12px 16px",
          background: "transparent",
          border: 0,
          cursor: "pointer",
          textAlign: "left",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "baseline",
            gap: 12,
            flex: 1,
            minWidth: 0,
          }}
        >
          <span
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: "#1f2633",
              flexShrink: 0,
            }}
          >
            <GauInText>Top up GAUs</GauInText>{" "}
          </span>
          <span
            data-test="auto-topup-summary"
            style={{
              fontSize: 12,
              color: "#6c7689",
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {summaryText.includes("GAU") ? (
              <GauInText>{summaryText}</GauInText>
            ) : (
              summaryText
            )}
          </span>
        </div>
        {/* Balance pill — only renders when this cycle has had at least one
            top up grant. Emerald when there's still balance, amber when fully
            consumed. Hidden on a fresh setup so the header doesn't read "0 left"
            before any purchase has happened. */}
        {(() => {
          const topupRemaining = data.stats?.credits_remaining ?? 0;
          const hasTopupActivity = data.recent_topups.length > 0;
          if (!hasTopupActivity) return null;
          // Highest priority: cap-skip pending. Auto top-up won't fire again this
          // cycle regardless of remaining balance, so the "X left" framing would be
          // misleading. Show an amber pill that signals the pause explicitly.
          if (showCapSkipBanner) {
            return (
              <span
                data-test="topup-balance-pill"
                data-state="cap-skip"
                title="Auto top-up is paused for the rest of this cycle — see panel for details"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                  padding: "1px 8px",
                  borderRadius: 999,
                  fontSize: 11,
                  fontWeight: 500,
                  flexShrink: 0,
                  marginLeft: 8,
                  background: "#fef3c7",
                  color: "#854d0e",
                }}
              >
                <span
                  aria-hidden="true"
                  style={{
                    display: "inline-block",
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    background: "#f59e0b",
                  }}
                />
                Cap reached · paused this cycle
              </span>
            );
          }
          if (topupRemaining > 0) {
            return (
              <span
                data-test="topup-balance-pill"
                data-state="remaining"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                  padding: "1px 8px",
                  borderRadius: 999,
                  fontSize: 11,
                  fontWeight: 500,
                  flexShrink: 0,
                  marginLeft: 8,
                  background: "#d1fae5",
                  color: "#047857",
                }}
              >
                <span
                  aria-hidden="true"
                  style={{
                    display: "inline-block",
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    background: "#10b981",
                  }}
                />
                {topupRemaining.toLocaleString()} left
              </span>
            );
          }
          return (
            <span
              data-test="topup-balance-pill"
              data-state="drained"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                padding: "1px 8px",
                borderRadius: 999,
                fontSize: 11,
                fontWeight: 500,
                flexShrink: 0,
                marginLeft: 8,
                background: "#fef3c7",
                color: "#92400e",
              }}
            >
              <span
                aria-hidden="true"
                style={{
                  display: "inline-block",
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  background: "#f59e0b",
                }}
              />
              All used
            </span>
          );
        })()}
        <ChevronRightIcon
          aria-hidden="true"
          className="size-4 shrink-0 text-[#6c7689] transition-transform duration-150"
          style={{
            transform: expanded ? "rotate(90deg)" : "rotate(0deg)",
            marginLeft: 12,
          }}
        />
      </button>
      {expanded ? (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 12,
            padding: "0 16px 16px",
            borderTop: "1px solid #f1f3f5",
            paddingTop: 12,
          }}
        >
          {showNoPaymentBanner ? (
            <Banner tone="info">
              Add a payment method to enable top up.{" "}
              <button
                type="button"
                data-test="auto-topup-open-portal"
                onClick={handleOpenPortal}
                style={{
                  background: "transparent",
                  border: 0,
                  padding: 0,
                  color: "inherit",
                  textDecoration: "underline",
                  cursor: "pointer",
                }}
              >
                Update billing
              </button>
            </Banner>
          ) : null}

          {showAutoDisabledBanner ? (
            <Banner tone="error">
              {disabledReasonMessage(data.last_disabled_reason)}
              {data.last_disabled_at ? (
                <>
                  {" "}
                  <span style={{ opacity: 0.8 }}>
                    (disabled {formatDate(data.last_disabled_at)})
                  </span>
                </>
              ) : null}
            </Banner>
          ) : null}

          {showCapSkipBanner ? (
            <Banner tone="warning">
              <strong>Auto top-up is paused for the rest of this cycle.</strong>{" "}
              You&apos;ve purchased{" "}
              <strong>
                {data.stats?.credits_purchased_this_cycle.toLocaleString() ?? 0}
                {data.monthly_credit_cap != null
                  ? ` / ${data.monthly_credit_cap.toLocaleString()}`
                  : ""}
              </strong>{" "}
              <GauTerm plural /> this cycle, leaving only{" "}
              <strong>
                {(capRoomRemaining ?? 0).toLocaleString()} <GauTerm plural />
              </strong>{" "}
              of room — below the{" "}
              <strong>
                {data.min_partial_topup}-<GauTerm /> minimum
              </strong>{" "}
              we need to charge another pack.{" "}
              <button
                type="button"
                data-test="auto-topup-raise-cap"
                onClick={() => {
                  // Focus the Monthly team limit input and select its value so the admin
                  // can immediately type a new number. Cheap UX win — no separate
                  // wizard needed.
                  const el = document.querySelector<HTMLInputElement>(
                    '[data-test="auto-topup-cap"]',
                  );
                  if (el) {
                    el.focus();
                    el.select();
                    el.scrollIntoView({ block: "center", behavior: "smooth" });
                  }
                }}
                style={{
                  background: "transparent",
                  border: 0,
                  padding: 0,
                  color: "inherit",
                  textDecoration: "underline",
                  cursor: "pointer",
                  fontWeight: 600,
                }}
              >
                Raise cap
              </button>
            </Banner>
          ) : null}

          {/* Manual top up subsection — admin-initiated one-time purchase. */}
          <div
            data-test="manual-topup-subsection"
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 8,
            }}
          >
            <div
              style={{
                fontSize: 12,
                fontWeight: 600,
                color: "#1f2633",
              }}
            >
              Top up now
            </div>
            <div style={{ fontSize: 12, color: "#6c7689" }}>
              <GauInText>
                Buy GAUs and add them to your shared pool immediately. Top up
                GAUs will expire at the end of next billing cycle.
              </GauInText>
            </div>
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 6,
                marginTop: 4,
              }}
            >
              {MANUAL_PACKS.map((pack) => {
                const active = manualKind === "preset" && pack === manualPack;
                const disabled = isCharging || !data.has_default_payment_method;
                return (
                  <button
                    key={pack}
                    type="button"
                    data-test={`manual-topup-pack-${pack}`}
                    onClick={() => handleSelectPreset(pack)}
                    disabled={disabled}
                    style={{
                      height: 28,
                      padding: "0 12px",
                      borderRadius: 999,
                      border: active
                        ? "1px solid #1f2633"
                        : "1px solid #e8eaed",
                      background: active ? "#1f2633" : "#fff",
                      color: active ? "#fff" : "#1f2633",
                      fontSize: 12,
                      cursor: disabled ? "not-allowed" : "pointer",
                      opacity: disabled ? 0.5 : 1,
                    }}
                  >
                    {pack.toLocaleString()}
                  </button>
                );
              })}
              {(() => {
                const active = manualKind === "custom";
                const disabled = isCharging || !data.has_default_payment_method;
                return (
                  <button
                    type="button"
                    data-test="manual-topup-custom-toggle"
                    onClick={handleSelectCustom}
                    disabled={disabled}
                    style={{
                      height: 28,
                      padding: "0 12px",
                      borderRadius: 999,
                      border: active
                        ? "1px solid #1f2633"
                        : "1px solid #e8eaed",
                      background: active ? "#1f2633" : "#fff",
                      color: active ? "#fff" : "#1f2633",
                      fontSize: 12,
                      cursor: disabled ? "not-allowed" : "pointer",
                      opacity: disabled ? 0.5 : 1,
                    }}
                  >
                    Custom amount
                  </button>
                );
              })()}
            </div>
            {manualKind === "custom" ? (
              <div
                data-test="manual-topup-custom-row"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  marginTop: 6,
                }}
              >
                <input
                  data-test="manual-topup-custom-input"
                  type="number"
                  min={MANUAL_CUSTOM_MIN}
                  max={MANUAL_CUSTOM_MAX}
                  step={1}
                  value={customValue}
                  onChange={(e) => setCustomValue(e.target.value)}
                  onFocus={(e) => e.currentTarget.select()}
                  // While confirming, freeze the input so the admin can't
                  // edit the amount mid-commit. Cancel returns to the
                  // unarmed state with the value intact.
                  disabled={
                    isCharging ||
                    confirmingCharge ||
                    !data.has_default_payment_method
                  }
                  autoFocus
                  style={{
                    height: 32,
                    padding: "0 10px",
                    border: customError
                      ? "1px solid #b91c1c"
                      : "1px solid #e8eaed",
                    borderRadius: 6,
                    fontSize: 13,
                    width: 130,
                    background:
                      isCharging ||
                      confirmingCharge ||
                      !data.has_default_payment_method
                        ? "#f3f4f6"
                        : "#fff",
                  }}
                />
                <span
                  data-test="manual-topup-custom-helper"
                  style={{
                    fontSize: 11,
                    color: customError ? "#b91c1c" : "#6c7689",
                  }}
                >
                  {customError ? (
                    customError
                  ) : (
                    <GauInText>
                      {`GAUs · ${MANUAL_CUSTOM_MIN.toLocaleString()} – ${MANUAL_CUSTOM_MAX.toLocaleString()}`}
                    </GauInText>
                  )}
                </span>
              </div>
            ) : null}
            {/* Price preview + Add button row only renders when the admin has
                committed to a value (preset chip or valid custom amount). The
                resting state — no chip selected, no input — keeps this row
                hidden so the surface doesn't suggest a default purchase. */}
            {manualKind !== "none" ? (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 8,
                  marginTop: 4,
                }}
              >
                <span
                  data-test="manual-topup-price-preview"
                  style={{
                    fontSize: 12,
                    color: effectiveCredits == null ? "#a4aab7" : "#1f2633",
                  }}
                >
                  {effectiveCredits == null ? (
                    <GauInText>— GAUs = $—</GauInText>
                  ) : (
                    <GauInText>
                      {`${effectiveCredits.toLocaleString()} GAUs = ${formatUsd(effectiveCredits * data.unit_amount_cents)}`}
                    </GauInText>
                  )}{" "}
                </span>
                {confirmingCharge && !isCharging ? (
                  <div
                    data-test="manual-topup-confirm-row"
                    style={{ display: "flex", gap: 6 }}
                  >
                    <button
                      type="button"
                      data-test="manual-topup-cancel"
                      onClick={() => setConfirmingCharge(false)}
                      className="inline-flex h-9 cursor-pointer items-center justify-center rounded-md border border-[#e8eaed] bg-white px-3 text-sm font-medium text-[#454d5f] transition hover:bg-[#f9fafb]"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      data-test="manual-topup-charge"
                      onClick={handleChargeButtonClick}
                      disabled={
                        !data.has_default_payment_method ||
                        effectiveCredits == null
                      }
                      className="inline-flex h-9 cursor-pointer items-center justify-center rounded-md bg-black px-4 text-sm font-medium !text-white shadow-[0_1px_2px_rgba(0,0,0,0.05)] transition hover:bg-black/90 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {effectiveCredits == null
                        ? "Confirm and pay now"
                        : `Confirm and pay ${formatUsd(effectiveCredits * data.unit_amount_cents)} now`}
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    data-test="manual-topup-charge"
                    onClick={handleChargeButtonClick}
                    disabled={
                      isCharging ||
                      !data.has_default_payment_method ||
                      effectiveCredits == null
                    }
                    className="inline-flex h-9 cursor-pointer items-center justify-center rounded-md bg-black px-4 text-sm font-medium !text-white shadow-[0_1px_2px_rgba(0,0,0,0.05)] transition hover:bg-black/90 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {isCharging ? (
                      <GauInText>
                        {`Adding ${(effectiveCredits ?? 0).toLocaleString()} GAUs…`}
                      </GauInText>
                    ) : effectiveCredits == null ? (
                      <GauInText>Add GAUs</GauInText>
                    ) : (
                      <GauInText>
                        {`Add ${effectiveCredits.toLocaleString()} GAUs`}
                      </GauInText>
                    )}
                  </button>
                )}
              </div>
            ) : null}
            {manualError ? <Banner tone="error">{manualError}</Banner> : null}
            {manualSuccess ? (
              <Banner tone="info">{manualSuccess}</Banner>
            ) : null}
          </div>

          <div
            style={{
              borderTop: "1px solid #f1f3f5",
              margin: "4px 0",
            }}
          />

          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              fontSize: 13,
              color: "#1f2633",
            }}
          >
            <input
              type="checkbox"
              data-test="auto-topup-enabled"
              checked={form.enabled}
              disabled={!data.has_default_payment_method}
              onChange={(e) =>
                setForm((f) => ({ ...f, enabled: e.target.checked }))
              }
            />
            <span>
              <GauInText>
                Automatically buy GAUs when your pool runs out
              </GauInText>
            </span>
          </label>

          <div
            style={{
              display: "flex",
              flexDirection: "row",
              flexWrap: "wrap",
              gap: 12,
            }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 4,
                flex: "1 1 180px",
                minWidth: 0,
              }}
            >
              <label
                htmlFor="auto-topup-pack"
                style={{
                  fontSize: 12,
                  color: form.enabled ? "#6c7689" : "#b0b7c1",
                }}
              >
                <GauInText>GAUs per top up</GauInText>{" "}
              </label>
              <input
                id="auto-topup-pack"
                data-test="auto-topup-pack"
                type="number"
                min={PACK_MIN}
                max={PACK_MAX}
                step={1}
                value={form.packCredits}
                onChange={(e) =>
                  setForm((f) => ({ ...f, packCredits: e.target.value }))
                }
                disabled={!form.enabled}
                style={{
                  padding: "6px 8px",
                  border: "1px solid #e8eaed",
                  borderRadius: 4,
                  fontSize: 13,
                  width: "100%",
                  boxSizing: "border-box",
                  background: form.enabled ? "#fff" : "#f3f4f6",
                  color: form.enabled ? "#1f2633" : "#9ca3af",
                  cursor: form.enabled ? "text" : "not-allowed",
                }}
              />
              {packPreview ? (
                <span
                  data-test="auto-topup-pack-preview"
                  style={{
                    fontSize: 12,
                    color: form.enabled ? "#6c7689" : "#b0b7c1",
                  }}
                >
                  = {packPreview} per charge
                </span>
              ) : null}
              {errors.packCredits ? (
                <span
                  data-test="auto-topup-pack-error"
                  style={{ fontSize: 12, color: "#991b1b" }}
                >
                  {errors.packCredits}
                </span>
              ) : null}
            </div>

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 4,
                flex: "1 1 180px",
                minWidth: 0,
              }}
            >
              <label
                htmlFor="auto-topup-cap"
                style={{
                  fontSize: 12,
                  color: form.enabled ? "#6c7689" : "#b0b7c1",
                }}
              >
                Monthly team limit
              </label>
              <input
                id="auto-topup-cap"
                data-test="auto-topup-cap"
                type="number"
                min={
                  isWholeNumber(form.packCredits) ? Number(form.packCredits) : 0
                }
                step={1}
                value={form.monthlyCreditCap}
                onChange={(e) =>
                  setForm((f) => ({ ...f, monthlyCreditCap: e.target.value }))
                }
                disabled={!form.enabled}
                style={{
                  padding: "6px 8px",
                  border: "1px solid #e8eaed",
                  borderRadius: 4,
                  fontSize: 13,
                  width: "100%",
                  boxSizing: "border-box",
                  background: form.enabled ? "#fff" : "#f3f4f6",
                  color: form.enabled ? "#1f2633" : "#9ca3af",
                  cursor: form.enabled ? "text" : "not-allowed",
                }}
              />
              {capPreview ? (
                <span
                  data-test="auto-topup-cap-preview"
                  style={{
                    fontSize: 12,
                    color: form.enabled ? "#6c7689" : "#b0b7c1",
                  }}
                >
                  = {capPreview} / month
                </span>
              ) : null}
              {errors.monthlyCreditCap ? (
                <span
                  data-test="auto-topup-cap-error"
                  style={{ fontSize: 12, color: "#991b1b" }}
                >
                  {errors.monthlyCreditCap}
                </span>
              ) : null}
            </div>

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 4,
                flex: "1 1 180px",
                minWidth: 0,
              }}
            >
              <label
                htmlFor="auto-topup-trigger"
                style={{
                  fontSize: 12,
                  color: form.enabled ? "#6c7689" : "#b0b7c1",
                }}
              >
                <GauInText>
                  Top up when remaining GAUs drops below
                </GauInText>{" "}
              </label>
              <input
                id="auto-topup-trigger"
                data-test="auto-topup-trigger"
                type="number"
                min={0}
                max={
                  isWholeNumber(form.packCredits)
                    ? Number(form.packCredits)
                    : undefined
                }
                step={1}
                placeholder={String(data.trigger_buffer_default)}
                value={form.triggerBufferCredits}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    triggerBufferCredits: e.target.value,
                  }))
                }
                disabled={!form.enabled}
                style={{
                  padding: "6px 8px",
                  border: "1px solid #e8eaed",
                  borderRadius: 4,
                  fontSize: 13,
                  width: "100%",
                  boxSizing: "border-box",
                  background: form.enabled ? "#fff" : "#f3f4f6",
                  color: form.enabled ? "#1f2633" : "#9ca3af",
                  cursor: form.enabled ? "text" : "not-allowed",
                }}
              />
              {errors.triggerBufferCredits ? (
                <span
                  data-test="auto-topup-trigger-error"
                  style={{ fontSize: 12, color: "#991b1b" }}
                >
                  {errors.triggerBufferCredits}
                </span>
              ) : null}
            </div>
          </div>

          {data.stats ? (
            <div
              data-test="auto-topup-stats"
              style={{ fontSize: 12, color: "#6c7689" }}
            >
              This cycle:{" "}
              {/* Purchased stays a raw count: packs are sold whole, so it is
                  not a usage figure and does not go through formatGaus. Used
                  and remaining below are usage, and do. */}
              {data.stats.credits_purchased_this_cycle.toLocaleString()}{" "}
              <GauTerm plural /> purchased,{" "}
              {formatGaus(data.stats.credits_used_this_cycle)} used,{" "}
              {formatGaus(data.stats.credits_remaining)} remaining.
            </div>
          ) : null}

          {data.recent_topups.length > 0 ? (
            <div data-test="auto-topup-recent">
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: "#1f2633",
                  marginBottom: 4,
                }}
              >
                Recent top ups
              </div>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: 12,
                }}
              >
                <thead>
                  <tr style={{ textAlign: "left", color: "#6c7689" }}>
                    <th style={{ padding: "4px 8px", fontWeight: 500 }}>
                      Date
                    </th>
                    <th style={{ padding: "4px 8px", fontWeight: 500 }}>
                      Source
                    </th>
                    <th style={{ padding: "4px 8px", fontWeight: 500 }}>
                      <GauTerm plural />
                    </th>
                    <th style={{ padding: "4px 8px", fontWeight: 500 }}>
                      Remaining
                    </th>
                    <th style={{ padding: "4px 8px", fontWeight: 500 }}>
                      Expires
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data.recent_topups.map((r) => {
                    const used = Number(r.pool_credits_used) || 0;
                    const remaining = Math.max(0, r.quantity_purchased - used);
                    const isManual = r.source === "manual";
                    return (
                      <tr
                        key={r.id}
                        data-test={`auto-topup-recent-row-${r.id}`}
                        style={{ borderTop: "1px solid #f1f3f5" }}
                      >
                        <td style={{ padding: "4px 8px" }}>
                          {formatDate(r.cycle_start)}
                        </td>
                        <td style={{ padding: "4px 8px" }}>
                          <span
                            data-test={`auto-topup-recent-source-${r.id}`}
                            style={{
                              display: "inline-flex",
                              padding: "2px 6px",
                              borderRadius: 999,
                              fontSize: 10,
                              fontWeight: 600,
                              letterSpacing: "0.04em",
                              background: isManual ? "#d1fae5" : "#dbeafe",
                              color: isManual ? "#047857" : "#1d4ed8",
                            }}
                          >
                            {isManual ? "Manual" : "Auto"}
                          </span>
                        </td>
                        <td style={{ padding: "4px 8px" }}>
                          {r.quantity_purchased.toLocaleString()}
                        </td>
                        <td style={{ padding: "4px 8px" }}>
                          {remaining.toLocaleString()}
                        </td>
                        <td style={{ padding: "4px 8px" }}>
                          {r.ended_at ? formatDate(r.ended_at) : "—"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : null}

          {isDirty ? (
            <div
              data-test="auto-topup-actions"
              className="flex items-center justify-end gap-2 border-t border-[#f1f3f5] pt-3"
            >
              <button
                type="button"
                data-test="auto-topup-discard"
                onClick={handleDiscard}
                disabled={isSaving}
                className="inline-flex h-9 cursor-pointer items-center justify-center rounded-md border-0 bg-transparent px-3 text-sm font-medium text-[#454d5f] transition hover:bg-[#f3f4f6] disabled:cursor-not-allowed disabled:opacity-50"
              >
                Discard
              </button>
              <button
                type="button"
                data-test="auto-topup-save"
                onClick={handleSave}
                disabled={hasErrors || isSaving}
                className="inline-flex h-9 cursor-pointer items-center justify-center rounded-md bg-black px-4 text-sm font-medium !text-white shadow-[0_1px_2px_rgba(0,0,0,0.05)] transition hover:bg-black/90 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isSaving ? "Saving…" : "Save auto top up settings"}
              </button>
            </div>
          ) : null}

          {/* Dev tools button moved to /spending. Panel no longer carries
              its own dev affordance — keeps a single test surface. */}
        </div>
      ) : null}
    </div>
  );
}
